// The ordered top-level SECTIONS the current viewer can reach — the swipe roster for the app's root
// scroll-snap surface (the framed <main> in the app shell). Mirrors, in the SAME order, the section nav
// (app-section-nav.tsx — the topbar row on desktop, the bottom bar on mobile), so what you see in the nav,
// what you can swipe between, and which way a section link travels are one fact. Members are the section
// ROOT pathnames (`/teams`, `/jobs`, …); a URL deep inside a section (`/teams/42/board`) still resolves to
// its root via the engine's prefix match.
//
// It's also the bottom bar's own gate: `length <= 1` means there's nothing to navigate between, so the bar
// doesn't render at all (see app-bottom-nav.tsx).
//
// Role-gated exactly like the nav: a viewer who can see only one section gets a single-member roster, which
// the pager renders as an inert single slot (nothing to swipe) — so this is also the gate.

import { useSuspenseQuery } from '@tanstack/react-query'
import { trpc } from '@/main'
import { usePermissions } from '@/hooks/use-permissions'
import { useActiveEmployer } from '@/hooks/use-active-employer'

export function useTopLevelSections(): string[] {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const { isCompanyView, can } = usePermissions()
  const { activeEmployerId } = useActiveEmployer()

  if (!user) return []

  const isApplicant = user.type === 'applicant'
  const isSelfApplicant = isApplicant && !user.isEmployee
  const isEmployee = isApplicant && user.isEmployee
  const canAccessOrgs = can('orgs:read')

  // No manual memo: the React Compiler auto-memoizes this derivation, so the array keeps its identity while
  // the gates below are unchanged. That identity matters — the pager takes it as `group` and keys callbacks
  // and effects on it (see route-transition/scroll-transition-outlet, use-edge-swipe).
  const sections: string[] = []
  if (canAccessOrgs) sections.push('/orgs')
  if (isCompanyView) sections.push('/employees')
  if (isApplicant && !isCompanyView) sections.push('/report')
  // Position (employee, own view) and Jobs (company view) are the same /jobs root, shown in mutually
  // exclusive states — so it appears at most once.
  if ((isEmployee && activeEmployerId !== null && !isCompanyView) || isCompanyView) sections.push('/jobs')
  if (!isSelfApplicant && (!isEmployee || activeEmployerId !== null)) sections.push('/teams')
  if (!isSelfApplicant && (!isEmployee || isCompanyView)) sections.push('/offers')
  if (isCompanyView) sections.push('/market')

  return sections
}
